import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Menu, X, Wallet, Zap, ChevronDown, LogOut, User, Briefcase, Shield } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
    setUserMenuOpen(false);
  };

  return (
    <nav data-testid="navbar" style={{
      position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)",
      zIndex: 100, width: "calc(100% - 48px)", maxWidth: 900,
      background: "rgba(255,255,255,0.72)", backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border: "1px solid rgba(255,255,255,0.3)",
      boxShadow: "0 4px 24px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
      borderRadius: 100, padding: "0 24px", height: 52,
      display: "flex", alignItems: "center",
    }}>
      <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {/* Logo */}
        <Link to="/" data-testid="nav-logo" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 26, height: 26, borderRadius: 8,
            background: "linear-gradient(135deg, #005CEE, #8A4FFF)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "0.65rem", color: "#fff" }}>T</span>
          </div>
          <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "0.88rem", color: "var(--text-main)", letterSpacing: "-0.03em" }}>TIE</span>
        </Link>

        {/* Desktop nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 2 }} className="hide-mobile">
          {[
            { to: "/doer/feed", icon: Zap, label: "Find Tasks", testId: "nav-find-tasks" },
            ...(user ? [
              { to: "/my-work", icon: Briefcase, label: "My Work", testId: "nav-my-work" },
              { to: "/wallet", icon: Wallet, label: "Wallet", testId: "nav-wallet" },
              ...(user.role === "admin" ? [{ to: "/admin", icon: Shield, label: "Admin", testId: "nav-admin" }] : []),
            ] : []),
          ].map(({ to, icon: Icon, label, testId }) => (
            <Link key={to} to={to} data-testid={testId} style={{
              display: "flex", alignItems: "center", gap: 5, padding: "0.3rem 0.7rem", borderRadius: 100,
              fontFamily: "'Satoshi', sans-serif", fontSize: "0.8rem", fontWeight: 500,
              color: "var(--text-secondary)", textDecoration: "none", transition: "all 200ms",
            }}
              onMouseEnter={e => { e.currentTarget.style.color = "var(--text-main)"; e.currentTarget.style.background = "var(--bg-soft)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "var(--text-secondary)"; e.currentTarget.style.background = "transparent"; }}>
              <Icon size={13} strokeWidth={1.5} />{label}
            </Link>
          ))}
        </div>

        {/* User area */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {user ? (
            <div style={{ position: "relative" }}>
              <button data-testid="nav-user-menu" onClick={() => setUserMenuOpen(!userMenuOpen)} style={{
                display: "flex", alignItems: "center", gap: 6, padding: "0.25rem 0.6rem 0.25rem 0.25rem",
                borderRadius: 100, background: "var(--bg-soft)", border: "1px solid var(--bg-neutral)",
                cursor: "pointer", color: "var(--text-main)", transition: "all 200ms",
              }}>
                <div style={{
                  width: 24, height: 24, borderRadius: "50%",
                  background: "linear-gradient(135deg, #005CEE, #8A4FFF)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <User size={11} color="#fff" strokeWidth={2} />
                </div>
                <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", fontWeight: 500 }}>
                  {user.full_name?.split(" ")[0] || "User"}
                </span>
                <ChevronDown size={11} style={{ color: "var(--text-muted)", transform: userMenuOpen ? "rotate(180deg)" : "none", transition: "transform 200ms" }} />
              </button>
              {userMenuOpen && (
                <div style={{
                  position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: 180,
                  background: "var(--bg-elevated)", border: "1px solid var(--glass-border)",
                  borderRadius: 14, padding: 4, boxShadow: "0 16px 48px rgba(0,0,0,0.08)", zIndex: 200,
                }}>
                  {[
                    { to: "/wallet", icon: Wallet, label: "Wallet" },
                    { to: "/my-work", icon: Briefcase, label: "My Work" },
                  ].map(({ to, icon: Icon, label }) => (
                    <Link key={to} to={to} onClick={() => setUserMenuOpen(false)} style={{
                      display: "flex", alignItems: "center", gap: 8, padding: "0.5rem 0.8rem", borderRadius: 10,
                      textDecoration: "none", color: "var(--text-secondary)", fontFamily: "'Satoshi', sans-serif",
                      fontSize: "0.82rem", background: "transparent",
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = "var(--bg-soft)"; e.currentTarget.style.color = "var(--text-main)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-secondary)"; }}>
                      <Icon size={13} /> {label}
                    </Link>
                  ))}
                  <div style={{ height: 1, background: "var(--glass-border)", margin: "4px 8px" }} />
                  <button data-testid="nav-logout" onClick={handleLogout} style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "0.5rem 0.8rem",
                    borderRadius: 10, border: "none", background: "transparent", color: "var(--pink)",
                    fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", cursor: "pointer", textAlign: "left",
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,51,102,0.06)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <LogOut size={13} /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", gap: 6 }}>
              <Link to="/login" data-testid="nav-login" style={{
                padding: "0.3rem 1rem", fontSize: "0.8rem", textDecoration: "none", borderRadius: 100,
                color: "var(--text-secondary)", fontFamily: "'Satoshi', sans-serif", fontWeight: 500,
                transition: "color 200ms",
              }}
                onMouseEnter={e => e.currentTarget.style.color = "var(--text-main)"}
                onMouseLeave={e => e.currentTarget.style.color = "var(--text-secondary)"}>
                Sign in
              </Link>
              <Link to="/register" data-testid="nav-register" className="btn-primary" style={{ padding: "0.3rem 1rem", fontSize: "0.78rem", textDecoration: "none" }}>
                Get started
              </Link>
            </div>
          )}
          <button onClick={() => setMenuOpen(!menuOpen)} style={{ display: "none", background: "none", border: "none", color: "var(--text-main)", cursor: "pointer" }} className="show-mobile">
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
    </nav>
  );
}
